/**
 * Simplified Mock Device API
 * Always returns mock + real data combined with grouping and statistics
 */

import { getDeviceList as getDeviceListFromApi } from './api';
import { isAuthConfigured } from '../auth/authUtils';
import { processDeviceListData } from './grouping';
import type { 
  DeviceListItem, 
  DeviceInfo, 
  BatteryInfo, 
  DeviceListResponse,
  DeviceGroupData,
  DeviceListStatistics 
} from './types';

/**
 * Mock device configuration
 */
const MOCK_CONFIG = {
  GROUPS_COUNT: 5,
  DEVICES_PER_GROUP: 20,
  ENABLE_MOCK: true,
};

/**
 * Mock group names
 */
const MOCK_GROUP_NAMES = [
  'Seoul Fleet Alpha',
  'Busan Transport Beta', 
  'Daegu Logistics Gamma',
  'Incheon Delivery Delta',
  'Gwangju Express Epsilon'
];

/**
 * Mock device models
 */
const MOCK_DEVICE_MODELS = [
  'DR970X-2CH',
  'DR970X-1CH', 
  'DR900X-2CH',
  'DR900X-1CH',
  'DR750X-2CH'
];

/**
 * Communication types
 */
const COMM_TYPES: Array<DeviceInfo['communication_identifier']> = [
  'Wifi', 'Cat4', 'Cat-M1'
];

/**
 * Generate random PSN
 */
function generatePSN(groupIndex: number, deviceIndex: number): string {
  const groupId = (groupIndex + 1).toString().padStart(2, '0');
  const deviceId = (deviceIndex + 1).toString().padStart(3, '0');
  return `MOCK${groupId}${deviceId}`;
}

/**
 * Generate random battery info
 */
function generateBatteryInfo(): BatteryInfo {
  const percentage = Math.floor(Math.random() * 100);
  const celsius = Math.floor(Math.random() * 60) + 10; // 10-70°C
  
  return {
    ssid: `MOCK_BATTERY_${Math.random().toString(36).substr(2, 9)}`,
    celsius,
    battery_percent: percentage,
    createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    state: percentage > 70 ? 'latest' : percentage > 30 ? 'lateup1' : 'lateup2'
  };
}

/**
 * Generate random coordinates (South Korea region)
 */
function generateKoreanCoordinates(): { lat: number; lng: number } {
  // South Korea approximate bounds
  const latMin = 33.0;
  const latMax = 38.6;
  const lngMin = 125.0;
  const lngMax = 131.9;
  
  return {
    lat: latMin + Math.random() * (latMax - latMin),
    lng: lngMin + Math.random() * (lngMax - lngMin)
  };
}

/**
 * Generate mock device info
 */
function generateMockDevice(groupIndex: number, deviceIndex: number, groupName: string): DeviceInfo {
  const psn = generatePSN(groupIndex, deviceIndex);
  const isOnline = Math.random() > 0.3; // 70% online rate
  const hasVideo = Math.random() > 0.4; // 60% video sharing
  const isCloud = Math.random() > 0.5; // 50% cloud enabled
  const isParking = Math.random() > 0.8; // 20% parking mode
  
  const now = new Date();
  const regDate = new Date(now.getTime() - Math.random() * 365 * 24 * 60 * 60 * 1000);
  const loginDate = isOnline ? 
    new Date(now.getTime() - Math.random() * 24 * 60 * 60 * 1000) : // Last 24 hours for online
    new Date(now.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000); // Last 30 days for offline
  
  return {
    valid: 'valid',
    psn,
    model: MOCK_DEVICE_MODELS[Math.floor(Math.random() * MOCK_DEVICE_MODELS.length)],
    active: isOnline ? 'on' : 'off',
    dev_name: `${groupName.split(' ')[0]}-${(deviceIndex + 1).toString().padStart(3, '0')}`,
    battery: Math.random() > 0.2 ? generateBatteryInfo() : undefined, // 80% have battery info
    lb_server_name: `mock-lb-${Math.floor(Math.random() * 10)}`,
    lb_http_port: 80,
    lb_rtmp_port: 1935,
    share_video: hasVideo ? 'on' : 'off',
    share_audio: hasVideo ? (Math.random() > 0.5 ? 'on' : 'off') : 'off',
    share_dev_name: 'on',
    share_gps: isOnline ? 'on' : 'off',
    agree_gps: 'on',
    fw_ver: `${Math.floor(Math.random() * 3) + 1}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 100)}`,
    upload_server: isCloud ? `cloud-upload-${Math.floor(Math.random() * 5)}.fleeta.io` : undefined,
    upload_port: isCloud ? 443 : undefined,
    lang: 'eng',
    mode: isParking ? '1' : '0',
    communication_identifier: COMM_TYPES[Math.floor(Math.random() * COMM_TYPES.length)],
    temperature_level: Math.floor(Math.random() * 5) + 1,
    sensitivity_level: Math.floor(Math.random() * 5) + 1,
    dms_type: 'standard',
    dms_version: '1.0.0',
    reg_date: regDate.toISOString(),
    login_date: loginDate.toISOString(),
    iot_conn: isOnline ? 'on' : 'off',
    reg_category: isCloud ? 'cloud' : 'wifi',
    continentCode: 'AS',
    countryCode: 'KR',
    ip: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
    liveview_rating: Math.floor(Math.random() * 5) + 1,
    regInfoUpdateDT: new Date().toISOString(),
    countryCodeUpdateDT: new Date().toISOString(),
    profile_img: Math.random() > 0.7 ? `https://api.dicebear.com/7.x/identicon/svg?seed=${psn}` : undefined,
    profile_img_v2: Math.random() > 0.7 ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${psn}` : undefined,
    cldn: `cldn_${psn.toLowerCase()}`,
    ap_ssid: isCloud ? undefined : `MockWiFi_${deviceIndex}`,
    ap_pw: isCloud ? undefined : `mock_pass_${deviceIndex}`,
    require_time_sync: Math.random() > 0.5 ? 'on' : 'off'
  };
}

/**
 * Generate pure mock device list items with groups
 */
function generatePureMockDeviceList(): DeviceListItem[] {
  if (!MOCK_CONFIG.ENABLE_MOCK) {
    return [];
  }
  
  const mockDevices: DeviceListItem[] = [];
  
  // Generate devices for each group
  for (let groupIndex = 0; groupIndex < MOCK_CONFIG.GROUPS_COUNT; groupIndex++) {
    const groupName = MOCK_GROUP_NAMES[groupIndex];
    const groupManagementID = `MOCK_GROUP_${(groupIndex + 1).toString().padStart(3, '0')}`;
    const groupRegDT = new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString();
    
    // Generate devices for this group
    for (let deviceIndex = 0; deviceIndex < MOCK_CONFIG.DEVICES_PER_GROUP; deviceIndex++) {
      const device = generateMockDevice(groupIndex, deviceIndex, groupName);
      
      const deviceListItem: DeviceListItem = {
        groupManagementID,
        groupName,
        groupRegDT,
        device
      };
      
      mockDevices.push(deviceListItem);
    }
  }
  
  console.log('🎭 [MockAPI] Generated pure mock devices:', {
    totalDevices: mockDevices.length,
    groupsCount: MOCK_CONFIG.GROUPS_COUNT,
    devicesPerGroup: MOCK_CONFIG.DEVICES_PER_GROUP
  });
  
  return mockDevices;
}

/**
 * Get combined mock + real device list with grouping and statistics
 * Always returns mock data + real data (if available) combined
 * 
 * @returns Promise with combined device list response including groups and statistics
 */
export async function getDeviceList(): Promise<DeviceListResponse> {
  console.log('🔀 [MockAPI] Starting combined mock + real device list...');
  
  let mockDevices: DeviceListItem[] = [];
  let realDevices: DeviceListItem[] = [];
  
  // Step 1: Get mock devices
  try {
    mockDevices = generatePureMockDeviceList();
    console.log(`🎭 [MockAPI] Generated ${mockDevices.length} mock devices`);
  } catch (error) {
    console.error('❌ [MockAPI] Mock data generation failed:', error);
  }
  
  // Step 2: Get real devices (if authenticated)
  if (isAuthConfigured()) {
    try {
      console.log('📡 [MockAPI] Fetching real devices...');
      const realResponse = await getDeviceListFromApi(); // AuthStore에서 자동으로 auth 정보 사용
      
      if (realResponse.resultcode === 'BC_ERR_OK') {
        realDevices = realResponse.deviceListInfo || [];
        console.log(`✅ [MockAPI] Got ${realDevices.length} real devices`);
      } else {
        console.warn('⚠️ [MockAPI] Real API returned error:', realResponse.message);
      }
    } catch (error) {
      console.error('❌ [MockAPI] Real API failed:', error);
    }
  } else {
    console.log('⚠️ [MockAPI] Authentication not configured, using mock data only');
  }
  
  // Step 3: Combine data (real devices take priority for same PSN)
  const realPsns = new Set(realDevices.map(d => d.device.psn));
  const filteredMock = mockDevices.filter(d => !realPsns.has(d.device.psn));
  const combinedDevices = [...realDevices, ...filteredMock];
  
  console.log('🔀 [MockAPI] Data combination completed:', {
    mockDevices: mockDevices.length,
    realDevices: realDevices.length,
    filteredMock: filteredMock.length,
    combinedDevices: combinedDevices.length
  });
  
  // Step 4: Process grouping and statistics using shared function
  const { groups, statistics } = processDeviceListData(combinedDevices);
  
  console.log('📊 [MockAPI] Grouping and statistics completed:', {
    groups: groups.length,
    totalStats: statistics.total,
    onlineStats: statistics.online
  });
  
  // Step 5: Return in API response format
  return {
    resultcode: 'BC_ERR_OK',
    message: 'Combined mock and real data with grouping',
    deviceCount: combinedDevices.length,
    deviceLimitCount: 1000,
    sharedEventMap: 'on',
    deviceListInfo: combinedDevices,
    groupedData: {
      groups,
      statistics
    }
  };
}

/**
 * Export default function for backwards compatibility
 */
export default getDeviceList;